import { useCallback, useEffect, useRef, useState } from 'react';
import { DB } from '@tonic/central-specialties-utils';
import type { paths } from '@tonic/central-specialties-utils';
import { downloadCSVFile } from '../utils/downloadObjectsAsCSV.ts';
import { TimeframeData } from '../routes/root/TimeCardsPage/TimeCardsPage.tsx';

/**
 * GET endpoints that support the "archived tickets" CSV export shape: an
 * optional `{ startDate, endDate }` query filter and a text/csv-compatible
 * string response body. Add new endpoints here (not new hook logic) as long
 * as they share this shape.
 */
export type ArchiveExportEndpoint = Extract<
  keyof paths,
  '/part-requests/export-archived' | '/repair-requests/export-archived'
>;

/**
 * Shared "export archived tickets as CSV" behavior for the Parts and Repairs
 * archived-tickets modals: opening/closing the date-range picker, the network
 * call + CSV download, loading state, and restoring focus to the trigger
 * button once the picker closes.
 */
export const useArchiveExport = (
  endpoint: ArchiveExportEndpoint,
  filenamePrefix: string,
) => {
  const [isExportPickerOpen, setIsExportPickerOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const downloadButtonRef = useRef<any>(null);

  useEffect(() => {
    // When the DateRangePicker is closed, restore focus to the trigger button.
    if (!isExportPickerOpen) {
      // Delay until after re-render
      setTimeout(() => {
        downloadButtonRef.current?.focus?.();
      }, 0);
    }
  }, [isExportPickerOpen]);

  const openExportPicker = useCallback(() => setIsExportPickerOpen(true), []);
  const closeExportPicker = useCallback(() => setIsExportPickerOpen(false), []);

  const handleExport = useCallback(
    async (dateRangeTimeFrame: TimeframeData) => {
      const { startDate, endDate } = dateRangeTimeFrame;
      setExportLoading(true);
      try {
        // Only send timeframe query params if both dates are provided
        const response = await DB.GET(endpoint, {
          parseAs: 'text',
          params:
            startDate && endDate
              ? { query: { startDate, endDate } }
              : undefined,
        });
        if (!response.data) {
          throw new Error('Network response was not ok');
        }
        const name =
          filenamePrefix + (startDate || 'all') + '_to_' + (endDate || 'all');

        downloadCSVFile(response.data, name);
      } catch (error) {
        console.error('Download error:', error);
      } finally {
        setExportLoading(false);
      }
    },
    [endpoint, filenamePrefix],
  );

  return {
    isExportPickerOpen,
    exportLoading,
    downloadButtonRef,
    openExportPicker,
    closeExportPicker,
    handleExport,
  };
};
